/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { Box, CloseIcon, CrossIcon, Inscription, Text, UploadIcon } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ACCEPTED_FILE_TYPES, FileType } from '@/common/constants';
import useFilesInDocumentStore from '@/components/elements/DropScan/useFilesInDocument.store';

import PdfThumb from './PdfScanViewer/PdfThumb';
import TiffThumb from './TiffScanViewer/TiffThumb';
import {
  CloseButton,
  ImgSquare,
  ImgThumbInner,
  ImgThumbOuter,
  UploadContent,
  UploadText,
  Wrapper,
} from './ScanPreview.styles';

const PreviewTooltipWrapper = styled(Box)`
  position: absolute;
  right: -205px;
  top: -6px;
  z-index: 100;
  width: 210px;
  max-width: initial;
  &:before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    top: calc(100% - 48px);
    left: -5px;
    background-color: ${({ theme }) => theme.colors.background3};
    transform: rotate(-45deg);
    border-radius: 3px 0 0 0;
  }
`;
export const PreviewTooltip: React.FC<{ setShowTooltip: (val: boolean) => void }> = ({ setShowTooltip }) => (
  <PreviewTooltipWrapper
    round="container1Round"
    direction="row"
    background="background3"
    align="center"
    pad={{ vertical: 'spacing12', horizontal: 'spacing16' }}
  >
    <Box margin={{ right: 'spacing16' }}>
      <Text weight={500} size="small" color="textOnColor">
        Claim details will be scanned automatically from this file.
      </Text>
    </Box>
    <Box margin={{ right: 'spacing6' }} onClick={() => setShowTooltip(false)}>
      <CrossIcon color="iconOnColor" size="12px" />
    </Box>
  </PreviewTooltipWrapper>
);

export const ScanPreview = ({
  storeKey,
  isPreviewHidden,
  hasMainLabel,
  isSmallViewVersion,
  selectedIndex,
  isFlex,
  onSelect,
  onDelete,
  isAddDocFirst,
  children,
  isNumberOfImageHidden,
  overrideFiles,
  isThumbsTopPosition,
}: any) => {
  const {
    values: { files: storeFiles },
  } = useFilesInDocumentStore(storeKey);
  const [passwordProtectedIds, setPasswordProtectedIds] = React.useState<Record<string, boolean>>({});

  const files = overrideFiles ?? storeFiles;

  const thumbs = files?.map((file: any, index: number) => {
    const fileKey = file.preview || file.url;

    if (passwordProtectedIds[fileKey]) {
      return null;
    }

    let thumbContent: React.ReactNode;

    switch (file.type) {
      case FileType.PDF:
        thumbContent = (
          <PdfThumb
            testId={`scan-preview-item_${index}_image`}
            url={file.preview || file.url}
            alt={file.name}
            onPasswordProtectedChange={(isPasswordProtected) => {
              setPasswordProtectedIds((prevState) => {
                if (!!prevState[fileKey] === isPasswordProtected) {
                  return prevState;
                }
                return { ...prevState, [fileKey]: isPasswordProtected };
              });
            }}
          />
        );
        break;
      case FileType.TIF:
        thumbContent = (
          <TiffThumb
            data-testId={`scan-preview-item_${index}_image`}
            url={file.preview || file.url}
            alt={file.name}
          />
        );
        break;
      case FileType.TIFF:
        thumbContent = (
          <TiffThumb
            data-testId={`scan-preview-item_${index}_image`}
            url={file.preview || file.url}
            alt={file.name}
          />
        );
        break;
      default:
        thumbContent = (
          <ImgSquare
            data-testId={`scan-preview-item_${index}_image`}
            src={file.preview || file.url}
            alt={file.name}
          />
        );
    }

    return (
      <Box key={file.preview || file.url}>
        <ImgThumbOuter
          data-testId={`scan-preview-item_${index}`}
          active={index === selectedIndex}
          key={file.preview || file.url}
          onClick={() => onSelect(index)}
        >
          <ImgThumbInner>
            {onDelete ? (
              <CloseButton
                data-testId={`scan-preview-item_${index}_close-button`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
              >
                <CloseIcon data-testid="close_button_icon" color="iconOnColor" size="12px" />
              </CloseButton>
            ) : null}

            {isNumberOfImageHidden ? null : (
              <Box
                data-testId={`scan-preview-item_${index}_counter-wrapper`}
                width="20px"
                height="20px"
                background="background3"
                justify="center"
                align="center"
                round="button2Round"
                style={{ position: 'absolute' }}
              >
                <Inscription
                  size="12px"
                  data-testId={`scan-preview-item_${index}_counter`}
                  color="textOnColor"
                >
                  {index + 1}
                </Inscription>
              </Box>
            )}

            {index === 0 && isSmallViewVersion && (
              <Box
                background="button"
                justify="center"
                align="center"
                width="100%"
                round={{ corner: 'bottom', size: '5px' }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                }}
              >
                {hasMainLabel && (
                  <Inscription size="small" weight={500} color="textOnColor">
                    Main
                  </Inscription>
                )}
              </Box>
            )}
            {thumbContent}
          </ImgThumbInner>
        </ImgThumbOuter>
      </Box>
    );
  });

  return (
    <Wrapper
      isSmallViewVersion={isSmallViewVersion}
      direction={isSmallViewVersion || isThumbsTopPosition ? 'row' : 'column'}
      isFlex={isFlex}
    >
      {isAddDocFirst && children}
      {files?.length && !isPreviewHidden ? thumbs : null}
      {!isAddDocFirst && children}
    </Wrapper>
  );
};

export const ScanUpload = ({ onFilesAdded, addFileButtonRef, uploadButton }: any) => {
  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_TYPES as unknown as Accept,
    onDrop: (acceptedFiles) => {
      onFilesAdded(acceptedFiles);
    },
  });

  if (uploadButton) {
    return (
      <Box {...getRootProps()}>
        {uploadButton}
        <input {...getInputProps()} />
      </Box>
    );
  }

  return (
    <ImgThumbOuter {...getRootProps()} isScanUpload active={false}>
      <ImgThumbInner>
        <UploadContent ref={addFileButtonRef}>
          <input {...getInputProps()} />
          <UploadIcon color="iconAccent" />
          <UploadText>
            Add <br /> a File
          </UploadText>
        </UploadContent>
      </ImgThumbInner>
    </ImgThumbOuter>
  );
};

export default ScanPreview;
